import { Icon, IconButton, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import MDButton from "components/MDButton";
import MDBox from "components/MDBox";
import MDSelect from "components/MDSelect/MDSelect";
import MDTypography from "components/MDTypography";
import OrganizationUsersOptions from "modules/organization/OrganizationUsersOptions/OrganizationUsersOptions";
import React, { useMemo, useState } from "react";
import { Cell } from "react-table";
import { Task } from "generated/graphql";
import { useCompleteTask, useDeleteTask, useReassignTask } from "hooks/tasks/useTaskMutations";
import { getFullName } from "utils/people/getFullName";

export default function TaskActionsCell({ row }: Cell<Task>) {
  const task = row.original;

  const [openReassign, setOpenReassign] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  const [completeTask, { loading: completing }] = useCompleteTask();

  const [deleteTask, { loading: deleting }] = useDeleteTask();

  const [reassignTask, { loading: reassigning }] = useReassignTask();

  const selectedUserOption = useMemo(() => {
    if (!task.assignedUser) return undefined;
    return {
      label: getFullName(task.assignedUser),
      value: task.assignedUser.id,
      hasEmployee: task.assignedUser.hasEmployee ?? false,
    };
  }, [task.assignedUser]);

  const mutationLoading = completing || deleting || reassigning;

  // MDSelect now requires a `name` prop. Make it stable + unique per task row.
  const selectName = useMemo(() => `reassignUser.${task.id}`, [task.id]);

  const onSaveReassign = async () => {
    if (!selectedUserId) return;
    await reassignTask({
      variables: {
        input: { id: task.id, assignedUserId: selectedUserId },
      },
    });
    setOpenReassign(false);
  };

  return (
    <MDBox display="flex" gap={1} justifyContent="center">
      {task.status !== "completed" && (
        <IconButton
          disabled={mutationLoading}
          onClick={async () => {
            await completeTask({ variables: { id: task.id } });
          }}
          title="Mark complete"
        >
          <Icon color="success">check_circle</Icon>
        </IconButton>
      )}

      <IconButton
        disabled={mutationLoading}
        onClick={async () => {
          await deleteTask({ variables: { id: task.id } });
        }}
        title="Delete task"
      >
        <Icon color="error">delete</Icon>
      </IconButton>

      <IconButton
        disabled={mutationLoading}
        onClick={() => {
          setSelectedUserId(task.assignedUser?.id ?? null);
          setOpenReassign(true);
        }}
        title="Reassign"
      >
        <Icon>person</Icon>
      </IconButton>

      <Dialog open={openReassign} onClose={() => setOpenReassign(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reassign Task</DialogTitle>
        <DialogContent>
          <MDBox mt={1}>
            <MDTypography variant="caption">Assign to</MDTypography>
            <OrganizationUsersOptions queryOverride={{ first: 200 }} selected={selectedUserOption}>
              {({ organizationUsersOptions, loading: usersLoading }) => (
                <MDSelect
                  name={selectName}
                  fullWidth
                  value={selectedUserId ?? ""}
                  onChange={(e) => setSelectedUserId(e.target.value as string)}
                  options={organizationUsersOptions}
                  label="User"
                  readOnly={mutationLoading || usersLoading}
                />
              )}
            </OrganizationUsersOptions>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton
            color="secondary"
            variant="outlined"
            onClick={() => setOpenReassign(false)}
            disabled={mutationLoading}
          >
            Cancel
          </MDButton>
          <MDButton
            color="blue"
            onClick={onSaveReassign}
            disabled={mutationLoading || !selectedUserId}
          >
            Save
          </MDButton>
        </DialogActions>
      </Dialog>
    </MDBox>
  );
}
